import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { usePreCadastroAdmissaoQuery } from '../../../hooks/data';
import type { ColaboradorPendente } from '../../../models';
import { paraDataBR } from '../../../utils/dataInput';
import VincularPreCadastroModal from './VincularPreCadastroModal';

interface PreCadastroAdmissaoSectionProps {
  preCadastroId?: string | null;
  colaboradorPendente?: ColaboradorPendente | null;
  /** SI numa Admissão sem pré-cadastro: mostra o "Vincular pré-cadastro" (vínculo manual). */
  vinculoManual?: { solicitacaoId: string; nome: string; email: string } | null;
}

/** Lista fixa e curta, nesta ordem — só o que o SI realmente usa pra conferir/agir na
 * Admissão, sem rolar o formulário inteiro (decisão explícita do usuário, que já bateu o
 * olho nas respostas completas e pediu pra reduzir a este conjunto). Cada campo aceita
 * mais de uma chave porque a origem muda a pergunta exata — Etapa 2 (Recrutamento, dentro
 * do Digital) usa um texto, o Forms antigo do SharePoint usa outro (ver mapeamentoEtapa2.ts
 * x mapeamentoPreCadastro.ts). */
const CAMPOS_RESUMO: Array<{ label: string; chaves: string[] }> = [
  { label: 'Nome completo', chaves: ['Nome completo', 'Nome Completo:'] },
  { label: 'Data de nascimento', chaves: ['Data de nascimento', 'Data de Nascimento:'] },
  { label: 'CPF', chaves: ['CPF', 'CPF:'] },
  { label: 'RG', chaves: ['RG', 'RG:'] },
  { label: 'Telefone', chaves: ['Telefone para contato', 'Telefone para contato Informar DDD'] },
  {
    label: 'Endereço completo',
    chaves: [
      'Endereço completo',
      'Endereço Completo: Informar Rua, número, complemento, bairro, cidade, UF, CEP e complemento se tiver',
    ],
  },
  { label: 'E-mail pessoal', chaves: ['E-mail pessoal', 'E-mail pessoal:'] },
  { label: 'Tipo de contratação', chaves: ['Tipo de contrato de trabalho', 'Qual tipo de contrato de trabalho?'] },
  {
    label: 'Sobrenome de preferência para o e-mail',
    chaves: ['Qual sobrenome você gostaria que fosse usado no seu e-mail da Raro?'],
  },
];

/** Blocos do resumo, nesta ordem — por rótulo de CAMPOS_RESUMO (+ Cargo, que vem da abertura). */
const GRUPOS: Array<{ titulo: string; campos: string[] }> = [
  { titulo: 'Dados pessoais', campos: ['Nome completo', 'Data de nascimento', 'CPF', 'RG'] },
  { titulo: 'Contato', campos: ['Telefone', 'E-mail pessoal', 'Endereço completo'] },
  { titulo: 'Contratação', campos: ['Tipo de contratação', 'Cargo', 'Sobrenome de preferência para o e-mail'] },
];

/**
 * Mostra as respostas do Forms de admissão (RH) vinculadas automaticamente a esta
 * Solicitação (por e-mail pessoal, ver functions/src/si/onSolicitacaoWrite) — evita o
 * RH ter que copiar/colar e reenviar esses dados pro SI. Só SI/RH veem (gated no
 * componente pai, SolicitacaoDetailsContent) — a resposta bruta pode conter dado
 * sensível do candidato (CPF, RG, saúde).
 */
export default function PreCadastroAdmissaoSection({
  preCadastroId,
  colaboradorPendente,
  vinculoManual,
}: PreCadastroAdmissaoSectionProps) {
  const { data: preCadastro, isLoading } = usePreCadastroAdmissaoQuery(preCadastroId);
  const [expandido, setExpandido] = useState(false);
  const [vinculando, setVinculando] = useState(false);

  if (!preCadastroId && vinculoManual) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-5">
        <p className="font-montserrat font-medium text-gray-900">Pré-cadastro não vinculado</p>
        <p className="mt-1 text-xs text-gray-600">
          Nenhum formulário de admissão foi encontrado pelo e-mail pessoal informado na abertura
          {colaboradorPendente?.emailInformado ? ` (${colaboradorPendente.emailInformado})` : ''}. Se a pessoa usou outro
          e-mail no formulário, vincule manualmente.
        </p>
        <button type="button" className="btn-secondary mt-3 text-sm" onClick={() => setVinculando(true)}>
          Vincular pré-cadastro
        </button>
        <VincularPreCadastroModal
          solicitacaoId={vinculoManual.solicitacaoId}
          colaboradorPendente={colaboradorPendente}
          nomeSolicitacao={vinculoManual.nome}
          emailSolicitacao={vinculoManual.email}
          isOpen={vinculando}
          onClose={() => setVinculando(false)}
        />
      </div>
    );
  }

  if (!preCadastroId || isLoading || !preCadastro) return null;

  const perguntas = [
    ...CAMPOS_RESUMO.map(({ label, chaves }) => {
      const resposta = chaves.map((chave) => preCadastro.respostas[chave]).find(Boolean);
      return [label, label === 'Data de nascimento' && resposta ? paraDataBR(resposta) : resposta] as const;
    }),
    // Cargo não vem do pré-cadastro (candidato não preenche isso) — vem do que o RH
    // informou ao abrir a Admissão (ver colaboradorPendente/NovaSolicitacaoContent.tsx).
    ['Cargo', colaboradorPendente?.positionName] as const,
  ].filter(([, resposta]) => resposta);
  const respostaPorCampo = new Map<string, string>(perguntas.map(([pergunta, resposta]) => [pergunta, String(resposta)]));
  const grupos = GRUPOS.map((grupo) => ({
    ...grupo,
    itens: grupo.campos.filter((campo) => respostaPorCampo.has(campo)),
  })).filter((grupo) => grupo.itens.length > 0);

  return (
    <section className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        aria-expanded={expandido}
        className="flex w-full flex-wrap items-center justify-between gap-3 px-5 py-4 text-left"
        onClick={() => setExpandido((atual) => !atual)}
      >
        <span>
          <span className="block font-montserrat text-base font-semibold text-gray-900">Pré-cadastro</span>
          <span className="text-sm text-gray-600">
            Respostas do candidato antes da admissão ·{' '}
            {preCadastro.vinculoManual ? 'vinculadas manualmente pelo SI' : 'vinculadas pelo e-mail pessoal'}
          </span>
        </span>
        <span className="flex items-center gap-3">
          {preCadastro.aplicadoAoColaborador && (
            <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">
              Aplicado ao perfil
            </span>
          )}
          {expandido ? (
            <ChevronUpIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
          )}
        </span>
      </button>

      {expandido && (
        <div className="grid gap-5 border-t border-gray-100 px-5 py-4 lg:grid-cols-3">
          {/* Um cartão por bloco, uma linha por informação — nada solto. */}
          {grupos.map((grupo) => (
            <div key={grupo.titulo} className="overflow-hidden rounded-lg border border-gray-200">
              <p className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                {grupo.titulo}
              </p>
              <dl className="divide-y divide-gray-100 text-sm">
                {grupo.itens.map((campo) => (
                  <div key={campo} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 px-4 py-2.5">
                    <dt className="shrink-0 text-gray-500">
                      {campo === 'Sobrenome de preferência para o e-mail' ? 'Sobrenome pro e-mail' : campo}
                    </dt>
                    <dd className="m-0 min-w-0 break-words text-right font-medium text-gray-900">{respostaPorCampo.get(campo)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
